import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import { green, yellow } from '@mui/material/colors';
import { getShooters, getScores, getStages } from '../../api';
import { getLatestScoreForShooterPerStage } from '../../utils/scoring';

function pad(num, decimals) {
  return Number(num).toFixed(decimals);
}

function formatMinSec(totalSeconds) {
  const min = Math.floor(totalSeconds / 60);
  const sec = String(totalSeconds % 60).padStart(2, '0');
  return `${min}:${sec}`;
}

export default function Stats() {
  const { t } = useTranslation();
  const { match } = useOutletContext();
  const [tab, setTab] = useState(0);
  const [shooters, setShooters] = useState([]);
  const [scores, setScores] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showParts, setShowParts] = useState([]);
  const AUTO_REFRESH_SECONDS = 120;
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(AUTO_REFRESH_SECONDS);
  const inFlightRef = useRef(false);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);
      const promises = [
        getShooters(match.id),
        getScores(match.id),
      ];
      if (match.stages?.length) {
        promises.push(getStages(match.stages.join(',')));
      }
      const results = await Promise.all(promises);
      setShooters(results[0].data || []);
      const scoresData = results[1].data;
      setScores(Array.isArray(scoresData) ? scoresData : scoresData?.scores || []);
      if (results[2]) setStages(results[2].data || []);
    } catch {
      // ignore
    } finally {
      if (!silent) setLoading(false);
      setRefreshing(false);
      setSecondsUntilRefresh(AUTO_REFRESH_SECONDS);
      inFlightRef.current = false;
    }
  }, [match.id, match.stages]);

  useEffect(() => { loadData(); }, [match.id]);

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsUntilRefresh((s) => {
        if (s <= 1) {
          loadData({ silent: true });
          return AUTO_REFRESH_SECONDS;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const squads = match.squads || [];
  const activeShooters = shooters.filter((s) => !s.cancelledParticipation);
  const scorableShooters = activeShooters.filter((s) => s['power-factor'] !== 'invalid');
  const dqShooterIds = new Set(scores.filter((s) => s.dq).map((s) => s.shooterId));

  // Use getLatestScoreForShooterPerStage to filter deleted scores and keep only latest per shooter
  const latestScoresPerStage = getLatestScoreForShooterPerStage({ scores, numStages: stages.length, stages });
  // latestScoresPerStage is 0-indexed array, convert to 1-based map for consistency
  const scoresByStageIdx = {};
  latestScoresPerStage.forEach((stageScores, i) => { scoresByStageIdx[i + 1] = stageScores; });

  const togglePart = (key) => {
    setShowParts((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  // ── Tab 0: Match Progress ──
  const renderProgress = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('stats.progressDetails')}
      </Typography>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 80 }} />
              {stages.map((stage, i) => (
                <TableCell key={stage.id} align="center" sx={{ minWidth: 40, px: 0.5 }}>
                  {i + 1}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {squads.map((squad) => {
              const squadShooters = activeShooters
                .filter((s) => s.squad === squad.id)
                .filter((s) => !dqShooterIds.has(s.id));
              if (squadShooters.length === 0) return null;

              return (
                <TableRow key={squad.id}>
                  <TableCell sx={{ py: 0.5 }}>{squad.title}</TableCell>
                  {stages.map((stage, stageIdx) => {
                    const stageScores = scoresByStageIdx[stageIdx + 1] || [];
                    const briefing = squadShooters.some((shooter) =>
                      ((shooter.equipmentChecks || [])[stageIdx] || {}).check
                      || stageScores.find((s) => s.shooterId === shooter.id),
                    );
                    const allScored = squadShooters.every((shooter) => {
                      if (((shooter.equipmentChecks || [])[stageIdx] || {}).check === 'absent') return true;
                      return stageScores.find((s) => s.shooterId === shooter.id);
                    });

                    const bgColor = allScored ? green[500] : briefing ? yellow[100] : undefined;

                    const toScore = squadShooters.filter((shooter) => {
                      if (((shooter.equipmentChecks || [])[stageIdx] || {}).check === 'absent') return false;
                      return !stageScores.find((s) => s.shooterId === shooter.id);
                    });
                    const absent = squadShooters.filter((shooter) =>
                      ((shooter.equipmentChecks || [])[stageIdx] || {}).check === 'absent',
                    );

                    return (
                      <Tooltip
                        key={stage.id}
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {t('stats.progressTooltip', { squad: squad.title, stage: stageIdx + 1 })}
                            </Typography>
                            {toScore.length > 0 && (
                              <>
                                <Typography variant="caption" fontWeight="bold">{t('stats.progressPending')}</Typography>
                                {toScore.map((s) => <div key={s.id}>{s.name}</div>)}
                              </>
                            )}
                            {absent.length > 0 && (
                              <>
                                <Typography variant="caption" fontWeight="bold">{t('stats.progressAbsent')}</Typography>
                                {absent.map((s) => <div key={s.id}>{s.name}</div>)}
                              </>
                            )}
                          </Box>
                        }
                        placement="top"
                      >
                        <TableCell
                          align="center"
                          sx={{
                            py: 0.5,
                            px: 0.5,
                            cursor: 'pointer',
                            bgcolor: bgColor,
                            border: '1px solid rgba(0,0,0,0.15)',
                          }}
                        >
                          &nbsp;
                        </TableCell>
                      </Tooltip>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // ── Tab 1: Stage Stats ──
  const renderStageStats = () => (
    <Box>
      {stages.map((stage, stageIdx) => {
        const stageScores = scoresByStageIdx[stageIdx + 1] || [];
        if (stageScores.length === 0) return null;

        const sorted = [...stageScores].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        const lastScore = sorted[sorted.length - 1];
        const lastScoreTime = lastScore?.timestamp ? new Date(lastScore.timestamp).toLocaleString() : '—';

        // Absent shooters
        const absent = scorableShooters.filter((s) =>
          ((s.equipmentChecks || [])[stageIdx] || {}).check === 'absent',
        );

        // Average score time
        const avgTime = stageScores.reduce((sum, s) => sum + (s.time || 0), 0) / stageScores.length;

        // Average gap between shooters (excluding top/bottom 25%)
        const gaps = [];
        for (let i = 1; i < sorted.length; i++) {
          if (sorted[i].timestamp && sorted[i - 1].timestamp) {
            gaps.push((sorted[i].timestamp - sorted[i - 1].timestamp) / 1000);
          }
        }
        gaps.sort((a, b) => a - b);
        const trimStart = Math.floor(gaps.length / 4);
        const trimEnd = Math.floor((gaps.length * 3) / 4);
        const midGaps = gaps.slice(trimStart, trimEnd);
        const avgGap = midGaps.length > 0
          ? Math.round(midGaps.reduce((s, g) => s + g, 0) / midGaps.length)
          : 0;

        // Fastest / slowest shooter
        const fastest = stageScores.reduce((best, s) => (!best || s.time < best.time) ? s : best, null);
        const slowest = stageScores.reduce((worst, s) => (!worst || s.time > worst.time) ? s : worst, null);
        const fastestShooter = fastest ? shooters.find((s) => s.id === fastest.shooterId) : null;
        const slowestShooter = slowest ? shooters.find((s) => s.id === slowest.shooterId) : null;

        return (
          <Paper key={stage.id} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {stageIdx + 1}. {stage.title}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <StatRow label={t('stats.lastScoreTime')} value={lastScoreTime} />
              <StatRow label={t('stats.scored')} value={`${stageScores.length} / ${scorableShooters.length}`} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2">{t('stats.absent')}</Typography>
                <Typography variant="body2" fontWeight="bold">{absent.length}</Typography>
                {absent.length > 0 && (
                  <Button size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => togglePart(`absent-${stageIdx}`)}>
                    {showParts.includes(`absent-${stageIdx}`) ? t('common.close') : t('stats.who')}
                  </Button>
                )}
              </Box>
              {showParts.includes(`absent-${stageIdx}`) && absent.length > 0 && (
                <Typography variant="body2" sx={{ pl: 1 }}>{absent.map((a) => a.name).join(' | ')}</Typography>
              )}

              <StatRow label={t('stats.avgScoreTime')} value={`${pad(avgTime, 2)}s`} />
              <StatRow label={t('stats.avgGap')} value={formatMinSec(avgGap)} />
              {fastestShooter && <StatRow label={t('stats.fastest')} value={`${fastestShooter.name} (${pad(fastest.time, 2)}s)`} />}
              {slowestShooter && <StatRow label={t('stats.slowest')} value={`${slowestShooter.name} (${pad(slowest.time, 2)}s)`} />}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  // ── Tab 2: Financial Stats ──
  const renderFinancial = () => {
    const paid = activeShooters.filter((s) => s.paid);
    const unpaid = activeShooters.filter((s) => !s.paid);
    const scoredShooterIds = new Set(scores.map((s) => s.shooterId));
    const paidDidntShoot = paid.filter((s) => !scoredShooterIds.has(s.id));

    const byDivision = {};
    activeShooters.forEach((s) => {
      const div = s.division || 'unknown';
      if (!byDivision[div]) byDivision[div] = { total: 0, paid: 0 };
      byDivision[div].total++;
      if (s.paid) byDivision[div].paid++;
    });

    return (
      <Box>
        <Paper sx={{ p: 2, mb: 2 }}>
          <StatRow label={t('stats.numPaid')} value={paid.length} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">{t('stats.numPaidDidntShoot')}</Typography>
            <Typography variant="body2" fontWeight="bold">{paidDidntShoot.length}</Typography>
            {paidDidntShoot.length > 0 && (
              <Button size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => togglePart('paid-no-shoot')}>
                {showParts.includes('paid-no-shoot') ? t('common.close') : t('stats.who')}
              </Button>
            )}
          </Box>
          {showParts.includes('paid-no-shoot') && paidDidntShoot.length > 0 && (
            <Typography variant="body2" sx={{ pl: 1 }}>{paidDidntShoot.map((s) => s.name).join(' | ')}</Typography>
          )}
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('stats.division')}</TableCell>
                <TableCell align="center">{t('stats.total')}</TableCell>
                <TableCell align="center">{t('stats.paid')}</TableCell>
                <TableCell align="center">{t('stats.unpaid')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(byDivision).sort(([, a], [, b]) => b.total - a.total).map(([div, data]) => (
                <TableRow key={div}>
                  <TableCell>{t(`divisions.${div}`, div)}</TableCell>
                  <TableCell align="center">{data.total}</TableCell>
                  <TableCell align="center">{data.paid}</TableCell>
                  <TableCell align="center">{data.total - data.paid}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Tab 3: Match Overview ──
  const renderOverview = () => {
    const totalScoresExpected = scorableShooters.length * stages.length;
    const totalScoresDone = scores.length;
    const matchPct = totalScoresExpected ? Math.round((totalScoresDone / totalScoresExpected) * 100) : 0;

    // Division breakdown
    const byDivision = {};
    activeShooters.forEach((s) => {
      const div = s.division || 'unknown';
      byDivision[div] = (byDivision[div] || 0) + 1;
    });

    // Category breakdown
    const byCategory = {};
    activeShooters.forEach((s) => {
      const cat = s.category || 'regular';
      byCategory[cat] = (byCategory[cat] || 0) + 1;
    });

    // DQ count
    const dqCount = dqShooterIds.size;

    // Clubs
    const clubs = new Set(activeShooters.map((s) => s.club).filter(Boolean));

    return (
      <Box>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>{t('stats.matchOverview')}</Typography>
          <StatRow label={t('stats.totalShooters')} value={activeShooters.length} />
          <StatRow label={t('stats.totalStages')} value={stages.length} />
          <StatRow label={t('stats.totalSquads')} value={squads.length} />
          <StatRow label={t('stats.totalClubs')} value={clubs.size} />
          <StatRow label={t('stats.dqCount')} value={dqCount} />
          <StatRow label={t('stats.cancelled')} value={shooters.filter((s) => s.cancelledParticipation).length} />
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>{t('stats.matchCompletion')}: {matchPct}%</Typography>
            <LinearProgress variant="determinate" value={matchPct} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('stats.byDivision')}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {Object.entries(byDivision).sort(([, a], [, b]) => b - a).map(([div, count]) => (
              <Chip key={div} label={`${t(`divisions.${div}`, div)}: ${count}`} size="small" variant="outlined" />
            ))}
          </Box>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('stats.byCategory')}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {Object.entries(byCategory).sort(([, a], [, b]) => b - a).map(([cat, count]) => (
              <Chip key={cat} label={`${t(`categories.${cat}`, cat)}: ${count}`} size="small" variant="outlined" />
            ))}
          </Box>
        </Paper>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography variant="h5">
          {t('stats.title')}
        </Typography>
        <Tooltip title={t('stats.refresh', 'Refresh')}>
          <span>
            <IconButton onClick={() => loadData({ silent: true })} disabled={refreshing} size="small">
              {refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Chip
          size="small"
          variant="outlined"
          label={t('stats.autoRefreshIn', 'Auto refresh in {{time}}', { time: formatMinSec(secondsUntilRefresh) })}
          sx={{ direction: 'ltr' }}
        />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab label={t('stats.progress')} />
        <Tab label={t('stats.stageStats')} />
        <Tab label={t('stats.financial')} />
        <Tab label={t('stats.overview')} />
      </Tabs>

      {tab === 0 && renderProgress()}
      {tab === 1 && renderStageStats()}
      {tab === 2 && renderFinancial()}
      {tab === 3 && renderOverview()}
    </Box>
  );
}

function StatRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" fontWeight="bold">{value}</Typography>
    </Box>
  );
}
